import sort from 'utils/sort';
import { ImResponse, storeHomeV2Client } from '..';

export const permissionNameIdMap: Record<General.PermissionName, General.PermissionId> = {
  基础聊天权限: 1,
  管理员视角: 2,
  订单敏感信息: 3,
  编辑优惠券: 4,
  弃单处理页面: 5,
  操作弃单: 6,
  访问评价聚合页: 35,
  履约数据: 9,
  履约数据导出: 10,
  '绩效报告-查看个人绩效': 11,
  '绩效报告-查看部门绩效': 12,
  绩效报告导出: 13,
  '快捷回复-个人模版': 14,
  '快捷回复-团队模版': 15,
  超时自动回复: 16,
  重要提醒: 17,
  WA消息模版: 18,
  AI智能接待: 19,
  配置催单催付任务: 30,
  配置缺货提醒任务: 31,
  配置物流呵护任务: 32,
  配置弃单挽回任务: 33,
  配置评价管理任务: 34,
  帐号管理: 36,
  店铺管理: 37,
  买家管理: 38,
  进店欢迎语: 29,
  配置接待评分任务: 39,
  访问待办管理中心: 40,
  查看待办数据: 41,
  导出待办数据: 42,
  配置暂停自动回复: 43,
  标记商品缺货: 44,
  敏感词: 45,
  商品组: 46,
  创建补发单: 47,
  审核补发单: 48,
  查看AI问答数据: 49,
  导出AI问答数据: 50,
  查看平台数据: 51,
  导出平台数据: 52,
  商品知识: 53,
  提交AI无法回复问题: 54,
  提交AI回复错误问题: 55,
  查看活动数据: 56,
  导出活动数据: 57,
};

export interface PermissionV2 {
  permissionId: number;
  permissionName: General.PermissionName;
  permissionGroupName: General.PermissionGroupName;
  storeScope: General.StoreScope;
  __LOCAL__: {
    disabled?: boolean;
    disabledScopes?: boolean;
  };
}

export const permissionsCannotChange: General.PermissionName[] = [
  '快捷回复-个人模版',
  '基础聊天权限',
];

export const scopesCannotChange: General.PermissionName[] = [
  '基础聊天权限',
  '快捷回复-个人模版',
  '店铺管理',
  '帐号管理',
  '履约数据导出',
  '绩效报告-查看个人绩效',
  '绩效报告-查看部门绩效',
  '绩效报告导出',
  '买家管理',
  '敏感词',
  '商品组',
  '商品知识',
  '提交AI无法回复问题',
  '提交AI回复错误问题',
];
/**
 * 某权限依赖的前置权限项
 */
export const permissionDependencies: Partial<General.PermissionDeps> = {
  操作弃单: ['弃单处理页面'],
  '绩效报告-查看部门绩效': ['绩效报告-查看个人绩效'],
  绩效报告导出: ['绩效报告-查看个人绩效', '绩效报告-查看部门绩效'],
  履约数据导出: ['履约数据'],
  导出待办数据: ['查看待办数据'],
  导出AI问答数据: ['查看AI问答数据'],
  导出平台数据: ['查看平台数据'],
  导出活动数据: ['查看活动数据'],
};

/**
 * 查找依赖该权限作为前置条件的权限项
 */
export const permissionDependents = Object.keys(permissionDependencies).reduce(
  (res, permissionName) => {
    const deps = permissionDependencies[permissionName];
    deps?.forEach((dependentName) => {
      const record = res[dependentName];
      if (!record) {
        // @ts-expect-error General.PermissionDeps 依赖集需要过滤本身，使用了Exclude,但是Exclude把never引入了导致识别错误
        res[dependentName] = [permissionName];
      } else {
        // @ts-expect-error General.PermissionDeps 依赖集需要过滤本身，使用了Exclude,但是Exclude把never引入了导致识别错误
        record.push(permissionName);
      }
      return res;
    });
    return res;
  },
  {} as Partial<General.PermissionDeps>,
);

const filterPermissions = (permissions: PermissionV2[]) => {
  return permissions.filter(
    ({ permissionName }) => permissionNameIdMap[permissionName] !== undefined,
  );
};

const permissionsOrder: General.PermissionName[] = [
  '快捷回复-团队模版',
  '超时自动回复',
  '进店欢迎语',
  '配置接待评分任务',
  '重要提醒',
  'WA消息模版',
  'AI智能接待',
  '配置催单催付任务',
  '配置缺货提醒任务',
  '配置物流呵护任务',
  '配置弃单挽回任务',
  '配置评价管理任务',
  '配置暂停自动回复',
];

export const getNewPermissionsV2 = async () => {
  const resp = await storeHomeV2Client.get<ImResponse<{ permissions: PermissionV2[] }>>(
    '/admin/new_permissions',
  );

  const { permissions = [] } = resp.data.data;

  return (
    sort(filterPermissions(permissions), 'permissionName', permissionsOrder).map((p) => {
      return {
        ...p,
        __LOCAL__: {
          disabled: permissionsCannotChange.includes(p.permissionName),
          disabledScopes: scopesCannotChange.includes(p.permissionName),
        },
      };
    }) ?? []
  );
};
